// Defensive Support Score (DSS) — Pitcher Luck Meter
// Measures whether a pitcher's ERA reflects actual performance or defensive/luck factors.
// FIP isolates pitcher-controlled outcomes (K, BB, HBP, HR); BABIP captures luck on balls in play.

export const FIP_CONSTANT = 3.10; // youth league baseline (MLB uses ~3.2)

export function calculateFIP(
  totalK: number,
  totalBB: number,
  totalHBP: number,
  totalHR: number,
  totalIP: number,
): number | null {
  if (totalIP <= 0) return null;
  return ((13 * totalHR) + (3 * (totalBB + totalHBP)) - (2 * totalK)) / totalIP + FIP_CONSTANT;
}

// BABIP = (H - HR) / (BF - BB - HBP - K - HR)
// BF here is total batters faced; denominator is "balls put in play"
export function calculateBABIP(
  totalH: number,
  totalHR: number,
  totalBF: number,
  totalBB: number,
  totalHBP: number,
  totalK: number,
): number | null {
  const denominator = totalBF - totalBB - totalHBP - totalK - totalHR;
  if (denominator <= 0) return null;
  const numerator = totalH - totalHR;
  if (numerator < 0) return null;
  return numerator / denominator;
}

const DSS_LABELS: { min: number; max: number; label: string }[] = [
  { min: 3.0,  max: 5.0,  label: 'Poor Defense Definitely Skewing Stats' },
  { min: 1.5,  max: 2.99, label: 'Pitching Better than Stats' },
  { min: -1.4, max: 1.49, label: 'Neutral:Both Defensive and Pitching Aligned' },
  { min: -2.9, max: -1.5, label: 'Likely Some Defense Help' },
  { min: -5.0, max: -3.0, label: 'Elite Defensive Support' },
];

function dssLabel(dss: number): string {
  for (const band of DSS_LABELS) {
    if (dss >= band.min && dss <= band.max) return band.label;
  }
  return 'Neutral';
}

export function calculateDSS(
  era: number,
  fip: number | null,
  babip: number | null,
  totalIP: number,
): { dss: number; label: string; confidence: 'low' | 'medium' | 'high' } | null {
  if (fip === null) return null;
  if (totalIP <= 0) return null;

  const eraFipGap = era - fip;
  const babipDev  = babip !== null ? babip - 0.300 : 0;
  const babipWeight = babip !== null ? 0.40 : 0;
  const eraWeight   = babip !== null ? 0.60 : 1.00;

  const rawDSS = (eraFipGap * eraWeight) + (babipDev * 10 * babipWeight);
  const dss = Math.max(-5, Math.min(5, rawDSS));

  const confidence: 'low' | 'medium' | 'high' =
    totalIP < 5 ? 'low' : totalIP < 15 ? 'medium' : 'high';

  return { dss, label: dssLabel(dss), confidence };
}

export function generateDSSInsight(
  playerFirstName: string,
  era: number,
  fip: number,
  babip: number | null,
  label: string,
  confidence: 'low' | 'medium' | 'high',
  totalIP: number,
): string {
  const gap = era - fip;
  const babipDisplay = babip !== null ? `.${Math.round(babip * 1000).toString().padStart(3, '0')}` : null;

  if (confidence === 'low') {
    return `Only ${totalIP.toFixed(1)} innings so far. Check back when ${playerFirstName} has had more work to get a reliable read.`;
  }

  if (gap > 2.0 && babip !== null && babip > 0.340) {
    return `${playerFirstName} is pitching better than his ERA suggests. His FIP of ${fip.toFixed(2)} reflects solid stuff, but a ${babipDisplay} BABIP means a lot of balls in play are finding holes, likely a defense factor, not a ${playerFirstName} factor.`;
  }

  if (gap > 1.0) {
    return `ERA of ${era.toFixed(2)} is running a bit high relative to his FIP (${fip.toFixed(2)}). He's competing well; some defensive or sequencing factors may be inflating the number.`;
  }

  if (gap < -1.5) {
    return `ERA looks strong, but FIP (${fip.toFixed(2)}) suggests he's received some defensive or sequencing help. Good results. Worth watching if they hold.`;
  }

  return `ERA (${era.toFixed(2)}) and FIP (${fip.toFixed(2)}) are well-matched. Results are reflecting his actual performance.`;
}
